import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight, CheckCircle, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenChecking, setTokenChecking] = useState(true);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) { setTokenChecking(false); return; }
    axios.get(`${API}/auth/reset-password/check?token=${token}`)
      .then(() => setTokenChecking(false))
      .catch(err => {
        const detail = err.response?.data?.detail;
        setTokenError(typeof detail === 'string' ? detail : 'Link tidak valid.');
        setTokenChecking(false);
      });
  }, [token]);

  const passwordStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 6) return { label: 'Terlalu pendek', color: 'bg-red-400', width: '25%' };
    if (pw.length < 8) return { label: 'Lemah', color: 'bg-orange-400', width: '50%' };
    if (pw.match(/[A-Z]/) && pw.match(/[0-9]/)) return { label: 'Kuat', color: 'bg-green-500', width: '100%' };
    return { label: 'Cukup', color: 'bg-yellow-400', width: '75%' };
  };

  const strength = passwordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Konfirmasi password tidak cocok.'); return; }
    if (password.length < 8) { setError('Password minimal 8 karakter.'); return; }
    if (!token) { setError('Token tidak valid. Minta reset password baru.'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Terjadi kesalahan. Coba lagi.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Loading saat cek token
  if (tokenChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          <p className="text-slate-500 text-sm">Memverifikasi link...</p>
        </div>
      </div>
    );
  }

  // Token tidak ada / tidak valid / sudah dipakai
  if (!token || tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 text-3xl">🔗</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Link Tidak Valid
          </h2>
          <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            {tokenError || 'Token reset password tidak ditemukan.'}
          </p>
          <Link to="/forgot-password"
            className="block w-full py-3 rounded-xl text-white font-semibold text-sm text-center shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
            Minta Link Reset Password Baru
          </Link>
          <Link to="/login" className="block mt-3 text-sm text-slate-400 hover:text-slate-600">
            Kembali ke Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1D4ED8 60%, #3B82F6 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FFFFFF 0%, transparent 50%)' }} />

        <div className="relative flex items-center gap-4">
          <img src="/logo-white.png" alt="RencanaGuru" className="h-16 w-16 object-contain drop-shadow-lg" />
          <div>
            <div className="font-bold text-2xl text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-400">.com</span>
            </div>
            <div className="text-blue-300 text-xs font-medium">Platform AI untuk Guru Indonesia</div>
          </div>
        </div>

        <div className="relative flex flex-col gap-6">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Buat Password<br />Baru Anda
            </h2>
            <p className="text-blue-200 text-lg leading-relaxed">
              Pilih password yang kuat dan mudah Anda ingat untuk mengamankan akun Anda.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {[
              '🔑 Minimal 8 karakter',
              '💡 Kombinasi huruf besar, kecil & angka lebih aman',
              '✅ Password lama tidak bisa digunakan lagi',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3.5">
                <span className="text-sm text-blue-100">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-blue-300 text-xs">© 2026 RencanaGuru.com</div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center gap-2 mb-8">
            <img src="/logo.png" alt="RencanaGuru" className="h-16 w-16 object-contain" />
            <span className="font-bold text-xl text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-500">.com</span>
            </span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-lg p-8">

            {success ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-blue-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Password Berhasil Direset!
                </h1>
                <p className="text-slate-500 text-sm mb-8">
                  Password baru Anda sudah aktif. Silakan login dengan password baru.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base shadow-md hover:-translate-y-0.5 transition-all"
                  style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
                >
                  Masuk ke Akun <ArrowRight size={18} />
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <KeyRound size={24} className="text-blue-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Buat Password Baru
                  </h1>
                  <p className="text-slate-500 text-sm">Password minimal 8 karakter.</p>
                </div>

                {error && (
                  <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">⚠</span>
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password Baru</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="rounded-xl border-blue-100 bg-white focus:border-blue-500 h-11 pr-11"
                        placeholder="Minimal 8 karakter"
                        autoFocus
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {strength && (
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: strength.width }} />
                        </div>
                        <span className="text-xs text-slate-500">{strength.label}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="confirm" className="text-slate-700 font-semibold text-sm">Konfirmasi Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm"
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className={`rounded-xl bg-white h-11 pr-11 transition-colors ${
                          confirmPassword && confirmPassword !== password
                            ? 'border-red-400 focus:border-red-500'
                            : confirmPassword && confirmPassword === password
                            ? 'border-green-400 focus:border-green-500'
                            : 'border-blue-100 focus:border-blue-500'
                        }`}
                        placeholder="Ulangi password baru"
                      />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {confirmPassword && confirmPassword !== password && (
                      <p className="text-xs text-red-500">Password tidak cocok.</p>
                    )}
                    {confirmPassword && confirmPassword === password && (
                      <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={12} /> Password cocok.</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !password || !confirmPassword}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Menyimpan...
                      </>
                    ) : (
                      <> Simpan Password Baru <ArrowRight size={18} /> </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            © 2026 RencanaGuru.com · Platform AI untuk Guru Indonesia
          </p>
        </div>
      </div>
    </div>
  );
};
